'use strict';

/**
 * The ONE declaration of a request key that names NO row of ours (coordinator ruling 2026-10-21, from D2's
 * connectors). `joiPrefixedId` declares an EPI-001 row id; this declares the things a key can be instead:
 *
 * - `external` — a provider's own id (Intuit's `realmId`, an external account id), named by its `provider`;
 * - `registryKey` — a registry's public key (connections' `:provider`), by its `pattern`. Never a closed `.valid()`
 *   set: a key the registry does not serve must reach the route and its 404, not a 422;
 * - `digest` — a SHA-256 digest (64 lowercase hex), or the `pattern` given;
 * - `ephemeral` — an EPI-001 id for state that is never a row (a Redis-held challenge session), named by its
 *   `entityType`, which the registry must mark ephemeral (`EPHEMERAL_ID_TYPES`); the pattern is that type's own prefix
 *   rule, never one the caller writes (coordinator ruling 2026-10-23);
 * - `replayKey` — a key its SENDER minted to make a re-send replay instead of repeat (a batch's key, a punch's
 *   Idempotency-Key), by its `pattern`, which is required: a minted key has a shape (coordinator ruling 2026-10-30).
 *
 * The schema carries a `.meta({ idKind: 'nonRow', kind, provider? })` tag, and the route-walking instruments read it
 * through `isNonRowIdSchema`, which accepts only a meta THIS helper created: a hand-written look-alike `.meta` is not
 * a declaration. The tag rides on the live schema (Joi keeps a meta's identity through chaining and `object.extract`);
 * `describe()` copies it, so read the tag from the schema, never from a description.
 *
 * @module @puffin-core/id-generator/nonRowId
 */

/** The kinds of non-row key, closed. */
const NON_ROW_ID_KINDS = Object.freeze([
  'external',
  'registryKey',
  'digest',
  'ephemeral',
  'replayKey',
]);

/** The kinds whose shape is the caller's `pattern`, so a call without one is refused. */
const PATTERN_REQUIRED_KINDS = Object.freeze(['registryKey', 'replayKey']);

/** A SHA-256 digest's hex text. */
const SHA256_HEX_PATTERN = /^[0-9a-f]{64}$/;

/** The longest provider id `external` admits when no `max` is given. */
const EXTERNAL_ID_MAX_LENGTH = 255;

/**
 * The registry, read when a call is made. `./index` requires this module while it loads, so a top-level require here
 * would see its exports before they exist.
 *
 * @returns {{EPHEMERAL_ID_TYPES: ReadonlyArray<string>, getIdPrefixPattern: (entityType: string) => RegExp}}
 */
function registry() {
  return require('./index');
}

/** Every tag this helper has stamped; a meta not in it was written by hand. */
const ISSUED_TAGS = new WeakSet();

/**
 * Check the options of one call, by kind.
 *
 * @param {{joi: object, kind: string, provider?: string, pattern?: RegExp}} options
 * @returns {void}
 */
function assertOptions({ joi, kind, provider, pattern, entityType }) {
  if (!joi || typeof joi.string !== 'function') {
    throw new Error(
      'joiNonRowId: options.joi is required (pass the Joi instance: `require("joi")`).',
    );
  }
  if (!NON_ROW_ID_KINDS.includes(kind)) {
    throw new Error(
      `joiNonRowId: kind must be one of ${NON_ROW_ID_KINDS.join(', ')}; got "${kind}".`,
    );
  }
  if (kind === 'external' && (typeof provider !== 'string' || provider.length === 0)) {
    throw new Error("joiNonRowId: options.provider is required for kind 'external'.");
  }
  if (kind !== 'external' && provider !== undefined) {
    throw new Error("joiNonRowId: options.provider is only for kind 'external'.");
  }
  if (PATTERN_REQUIRED_KINDS.includes(kind) && !(pattern instanceof RegExp)) {
    throw new Error(`joiNonRowId: options.pattern is required (a RegExp) for kind '${kind}'.`);
  }
  if (kind === 'ephemeral') assertEphemeral({ pattern, entityType });
  else if (entityType !== undefined) {
    throw new Error("joiNonRowId: options.entityType is only for kind 'ephemeral'.");
  }
}

/**
 * An `ephemeral` call names a type the registry marks ephemeral, and writes no pattern of its own.
 *
 * @param {{pattern?: RegExp, entityType?: string}} options
 * @returns {void}
 */
function assertEphemeral({ pattern, entityType }) {
  if (typeof entityType !== 'string' || entityType.length === 0) {
    throw new Error("joiNonRowId: options.entityType is required for kind 'ephemeral'.");
  }
  if (!registry().EPHEMERAL_ID_TYPES.includes(entityType)) {
    throw new Error(
      `joiNonRowId: "${entityType}" is not an ephemeral entity type (the registry's EPHEMERAL_ID_TYPES).`,
    );
  }
  if (pattern !== undefined) {
    throw new Error("joiNonRowId: options.pattern is not accepted for kind 'ephemeral'.");
  }
}

/**
 * The tag one call stamps.
 *
 * @param {{kind: string, provider?: string, entityType?: string}} options
 * @returns {Readonly<Object>}
 */
function tagOf({ kind, provider, entityType }) {
  if (kind === 'external') return Object.freeze({ idKind: 'nonRow', kind, provider });
  if (kind === 'ephemeral') return Object.freeze({ idKind: 'nonRow', kind, entityType });
  return Object.freeze({ idKind: 'nonRow', kind });
}

/**
 * A Joi string schema for a key that names no row of ours, tagged so the instruments read it as one.
 *
 * @param {{joi: object, kind: 'external'|'registryKey'|'digest'|'ephemeral'|'replayKey', provider?: string,
 *   pattern?: RegExp, entityType?: string, max?: number}} options - `provider` for `external` only; `pattern` required
 *   for `registryKey` and `replayKey`, refused for `ephemeral`, optional otherwise; `entityType` for `ephemeral` only; `max` bounds
 *   `external` (default 255) and any other kind it is given to.
 * @returns {object} A plain Joi string schema; chain `.required()`, `.label(...)` and the rest as usual.
 *
 * @example
 *   joiNonRowId({ joi: Joi, kind: 'external', provider: 'intuit' })            // realmId
 *   joiNonRowId({ joi: Joi, kind: 'registryKey', pattern: PROVIDER_KEY })      // :provider
 *   joiNonRowId({ joi: Joi, kind: 'digest' })                                  // :stateHash
 *   joiNonRowId({ joi: Joi, kind: 'ephemeral', entityType: 'challengesession' }) // challengeSessionId
 *   joiNonRowId({ joi: Joi, kind: 'replayKey', pattern: BATCH_KEY_PATTERN })   // batchId
 */
function joiNonRowId(options = {}) {
  assertOptions(options);
  const { joi, kind, pattern, entityType, max } = options;
  let schema = joi.string();
  const shape =
    kind === 'ephemeral'
      ? registry().getIdPrefixPattern(entityType)
      : pattern || (kind === 'digest' ? SHA256_HEX_PATTERN : null);
  if (shape) schema = schema.pattern(shape);
  const bound = max ?? (kind === 'external' ? EXTERNAL_ID_MAX_LENGTH : undefined);
  if (bound !== undefined) schema = schema.max(bound);
  const tag = tagOf(options);
  ISSUED_TAGS.add(tag);
  return schema.meta(tag);
}

/**
 * Whether a LIVE Joi schema carries a tag `joiNonRowId` stamped.
 *
 * @param {object} schema - A Joi schema (for a key of an object schema, `objectSchema.extract(key)`).
 * @returns {boolean}
 */
function isNonRowIdSchema(schema) {
  const metas = schema && schema.$_terms ? schema.$_terms.metas : undefined;
  return Array.isArray(metas) && metas.some((meta) => ISSUED_TAGS.has(meta));
}

module.exports = { NON_ROW_ID_KINDS, isNonRowIdSchema, joiNonRowId };
